import * as Telecom from '@servicetitan/telecom-rtcs-client';
import { userStatsFromAgentStats } from '../../types/ServiceUser';
import appManager from '@app/appManager';
import {
  ServiceSessionEventType,
  ServiceSessionUserEvent,
} from '../../types/ServiceEvents';
import requireUser, { rosterUser } from '../../lib/requireUser';
import log from '../../lib/log';
import isValidUser from '../../lib/isValidUser';
import { ServiceUserUtils } from '../../lib/serviceUserUtils';
import { UserActivityStatus } from '@api/generated/graphql';
import { RTCSEventType } from '../../../metrics/rtcsMetrics';
import { trackRTCSEventLatency } from '../../lib/trackRTCSEventLatency';
import {
  applyAgentSessionNow,
  enqueueNonSelfAgentSessionEvent,
  shouldBufferNonSelfAgentSession,
} from '../../lib/coalesceWorkspaceEvents';

/**
 * The hangup event indicates that a session has been hung-up
 * after being handled by the recipient.
 * @param event
 */
async function prepareAgentSessionTerminated(
  event: Telecom.AgentSessionTerminatedEvent,
) {
  if (!isValidUser(event.agentId)) {
    return undefined;
  }

  const user = await requireUser(event.agentId);
  return { user };
}

function commitAgentSessionTerminated(
  event: Telecom.AgentSessionTerminatedEvent,
  prepared: NonNullable<
    Awaited<ReturnType<typeof prepareAgentSessionTerminated>>
  >,
) {
  const user = rosterUser(event.agentId, prepared.user);
  log('rtcs:agent:session_terminated:' + user?.name, event);

  user.stats = userStatsFromAgentStats(event.stats);

  // This fix race condition with event: rtcs:agent:wrapup_started
  // if the user is in wrapup time, we should not change the activity status to None
  if (ServiceUserUtils.getActivityStatus(user) !== UserActivityStatus.WrapUp) {
    ServiceUserUtils.setEventActivityStatus(user, '');
  }

  appManager.events.emit<ServiceSessionUserEvent>(
    ServiceSessionEventType.HANGUP,
    {
      sessionId: event.conversationId,
      sessionSid: event.conversationSid,
      agentId: event.agentId,
      userId: user.id,
    },
  );
}

async function handleAgentSessionTerminated(
  event: Telecom.AgentSessionTerminatedEvent,
) {
  trackRTCSEventLatency(event.timestamp, RTCSEventType.SESSION_TERMINATED);
  if (!shouldBufferNonSelfAgentSession(event.agentId)) {
    return applyAgentSessionNow(event.agentId, (user) => {
      commitAgentSessionTerminated(event, { user });
    });
  }
  return enqueueNonSelfAgentSessionEvent(event.agentId, {
    prepare: () => prepareAgentSessionTerminated(event),
    commit: (value) => commitAgentSessionTerminated(event, value),
  });
}

export default handleAgentSessionTerminated;
